import React, { createContext, useState, useEffect, useContext, useCallback } from "react";
import type { ReactNode } from "react";
import { onAuthStateChanged } from "firebase/auth";
import { useQueryClient } from "@tanstack/react-query";
import { firebaseAuth } from "../firebase";

interface AppContextType {
  loading: boolean;
  initialLoadComplete: boolean;
  currentUserId: string | null;
  clearUserData: () => void;
}

// eslint-disable-next-line react-refresh/only-export-components
export const AppContext = createContext<AppContextType | undefined>(undefined);

// eslint-disable-next-line react-refresh/only-export-components
export const useApp = () => {
  const context = useContext(AppContext);
  if (!context) {
    throw new Error("useApp must be used within an AppProvider");
  }
  return context;
};

interface AppProviderProps {
  children: ReactNode;
}

export const AppProvider: React.FC<AppProviderProps> = ({ children }) => {
  const [loading, setLoading] = useState(true);
  const [initialLoadComplete, setInitialLoadComplete] = useState(false);
  const [currentUserId, setCurrentUserId] = useState<string | null>(null);

  const queryClient = useQueryClient();

  // Clear all cached data on logout
  const clearUserData = useCallback(() => {
    queryClient.removeQueries();
    setLoading(false);
  }, [queryClient]);

  // Monitor auth state
  useEffect(() => {
    const unsubscribe = onAuthStateChanged(firebaseAuth, (user) => {
      if (user) {
        setCurrentUserId(user.uid);
        setLoading(false);
        setInitialLoadComplete(true);
      } else {
        setCurrentUserId(null);
        clearUserData();

        if (initialLoadComplete) {
          setLoading(false);
        } else {
          setTimeout(() => {
            setLoading(false);
            setInitialLoadComplete(true);
          }, 1000);
        }
      }
    });

    return unsubscribe;
  }, [clearUserData, initialLoadComplete]);

  const value: AppContextType = {
    loading,
    initialLoadComplete,
    currentUserId,
    clearUserData,
  };

  return <AppContext.Provider value={value}>{children}</AppContext.Provider>;
};
